import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoSearch, IoClose } from "react-icons/io5";
import AppHeader from "../layout/appHeader";
import LogoutButton from "../common/logoutBtn";
import NavigationIndicator from "./navIndicator";
import RefreshAllSummaries from "../common/refreshAll";
import CustomInput from "../common/customInput";
import { customMaxInput } from "../../redux/utils";
import { Inceleme } from "../../data/inceleme.js";
import "./nav.css";

const BranchDetailsNavBar = ({
  showTickDetail,
  searchVal,
  setSearchVal,
  searchOpen,
  setSearchOpen,
}) => {
  const { t } = useTranslation();
  const branchIp = useParams().id;
  const location = useLocation();
  const segments = location.pathname.split("/");
  const url = segments[segments.length - 1];

  const navigation = [
    {
      name: t("nav.reports"),
      to: url !== branchIp ? "reports" : "",
      current: url === "reports" || url === branchIp,
    },
    { name: t("nav.compare"), to: "compare", current: url === "compare" },
    {
      name: t("nav.tickets"),
      to: "activeAdisyon",
      current: url === "activeAdisyon",
    },
    {
      name: t("nav.price_list"),
      to: "priceList",
      current: url === "priceList",
    },
    { name: t("nav.user"), to: "userActions", current: url === "userActions" },
    {
      name: t("nav.actions"),
      to: "actionsPage",
      current: url === "actionsPage",
    },
  ];

  useEffect(() => {
    if (!searchOpen && setSearchVal) {
      setSearchVal("");
    }
  }, [searchOpen, setSearchVal]);

  useEffect(() => {
    if (searchOpen) {
      document.body.classList.add("body_overflow_hidden");
    } else {
      document.body.classList.remove("body_overflow_hidden");
    }
    return () => {
      document.body.classList.remove("body_overflow_hidden");
    };
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen, setSearchOpen]);

  const searchButton = setSearchOpen ? (
    <SearchTrigger
      searchOpen={searchOpen}
      setSearchOpen={setSearchOpen}
      label={t("common.searchTicket")}
    />
  ) : null;

  return (
    <>
      <AppHeader
        breakpoint="lg"
        links={navigation}
        actions={
          <>
            {searchButton}
            <RefreshAllSummaries />
            <LogoutButton />
          </>
        }
        mobileActions={
          <>
            {searchButton}
            <RefreshAllSummaries />
          </>
        }
        panelExtra={<LogoutButton />}
      />

      {searchOpen && (
        <SearchPopUp
          setSearchOpen={setSearchOpen}
          setSearchVal={setSearchVal}
          showTickDetail={showTickDetail}
          placeholder={t("ticketDetails.enter")}
          searchVal={searchVal}
        />
      )}

      <NavigationIndicator />
    </>
  );
};

const SearchTrigger = ({ searchOpen, setSearchOpen, label }) => (
  <button
    type="button"
    className="cursor-pointer w-8 h-8 flex items-center justify-center overflow-hidden rounded-full bg-transparent p-0"
    onClick={() => setSearchOpen(!searchOpen)}
    aria-label={label}
    title={label}
    aria-expanded={searchOpen}
  >
    <span className="w-full h-full block" aria-hidden="true">
      <Inceleme width={"100%"} height={"100%"} />
    </span>
  </button>
);

const SearchPopUp = ({
  setSearchOpen,
  setSearchVal,
  showTickDetail,
  placeholder,
  searchVal,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.target.value) showTickDetail();
    }
  };

  return (
    <div
      className="fixed inset-0 flex justify-center items-start pt-32 sm:pt-48 px-[2%] z-[9999] bg-black/20"
      onClick={() => setSearchOpen(false)}
    >
      <div
        className="w-full max-w-[35rem] bg-[--nav-bg-1] flex flex-col items-center rounded-lg shadow-xl p-3"
        role="dialog"
        aria-modal="true"
        aria-label={t("common.searchTicket")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full flex items-center justify-between mb-3">
          <span className="text-[--gr-9] font-medium Poppins">
            {t("common.searchTicket")}
          </span>
          <button
            type="button"
            className="text-2xl p-1 text-white bg-[--primary] hover:bg-[--primary-hover] rounded-full flex items-center"
            onClick={() => setSearchOpen(false)}
            aria-label={t("common.close")}
          >
            <IoClose aria-hidden="true" />
          </button>
        </div>
        <div className="w-full max-w-[24rem] flex">
          <CustomInput
            placeholder={placeholder}
            ariaLabel={placeholder}
            type="number"
            inputMode="numeric"
            maxLength={9}
            onChange={(e) => setSearchVal(customMaxInput(e))}
            onKeyDown={handleKeyDown}
            value={searchVal}
            className={"border-r-0 rounded-r-none"}
            autoFocus={true}
          />
          <button
            type="button"
            className="text-xl text-white bg-[--primary] hover:bg-[--primary-hover] px-3 flex items-center rounded-r-lg border-0 border-l border-white border-solid cursor-pointer"
            onClick={() => showTickDetail()}
            aria-label={t("nav.search")}
          >
            <IoSearch aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchDetailsNavBar;
